import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { organizationAPI } from '../api/organizations';
import type { Organization } from '../api/organizations';
import { useAuth } from './AuthContext';

import { logger } from '../utils/logger';

interface OrganizationContextType {
  currentOrganization: Organization | null;
  organizations: Organization[];
  loading: boolean;
  error: string | null;
  switchOrganization: (orgId: string, options?: { reload?: boolean }) => Promise<void>;
  refreshOrganizations: () => Promise<void>;
  setCurrentOrganization: (org: Organization | null) => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const OrganizationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const activeUserId = useRef(user?.id);
  activeUserId.current = user?.id;
  const selectionVersion = useRef(0);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    logger.log('🏢 OrganizationContext: useEffect triggered');
    // AuthContext validates both cookie and legacy sessions. A cookie session
    // deliberately has no localStorage access token.
    if (user?.id) {
      logger.log('🏢 OrganizationContext: Loading organizations...');
      loadOrganizations();
    } else {
      setCurrentOrganization(null);
      setOrganizations([]);
      setError(null);
      setLoading(false);
    }

    // Listen for organization changes (e.g., after registration or login)
    const handleOrganizationsChanged = () => {
      logger.log('🔄 Organizations changed event received, refreshing...');
      loadOrganizations();
    };

    window.addEventListener('auth:organizations_changed', handleOrganizationsChanged as EventListener);

    return () => {
      window.removeEventListener('auth:organizations_changed', handleOrganizationsChanged as EventListener);
    };
  }, [user?.id]);

  const loadOrganizations = async () => {
    logger.log('🏢 loadOrganizations: Starting...');
    const requestedUserId = user?.id;
    const requestedSelectionVersion = selectionVersion.current;
    if (!requestedUserId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      logger.log('🏢 loadOrganizations: Calling API...');
      const orgs = await organizationAPI.getOrganizations();
      if (activeUserId.current !== requestedUserId || selectionVersion.current !== requestedSelectionVersion) return;
      logger.log('🏢 loadOrganizations: Received organizations:', orgs.length);
      setOrganizations(orgs);

      // Get current org from localStorage or use first org
      const currentOrgId = localStorage.getItem('current_organization_id');
      logger.log('🏢 loadOrganizations: Current org ID from localStorage:', currentOrgId);
      const current = orgs.find(o => o.id === currentOrgId) || orgs[0];

      if (current) {
        logger.log('🏢 loadOrganizations: Setting current organization:', current.name);
        setCurrentOrganization(current);
        localStorage.setItem('current_organization_id', current.id);
        localStorage.setItem('enterprise_org_id', current.id);

        // Store user role for API headers
        const userRole = (current as any).user_role || 'member';
        localStorage.setItem('enterprise_user_role', userRole);
        logger.log('🏢 loadOrganizations: Stored org headers:', {
          org_id: current.id,
          user_role: userRole
        });
      } else {
        logger.warn('🏢 loadOrganizations: No organizations found!');
      }
    } catch (err: any) {
      if (activeUserId.current !== requestedUserId || selectionVersion.current !== requestedSelectionVersion) return;
      logger.error('❌ Failed to load organizations:', err);
      // Don't set error if it's a 401 (user not authenticated)
      if (err.status !== 401) {
        setError(err.message || 'Failed to load organizations');
      }
    } finally {
      if (activeUserId.current === requestedUserId && selectionVersion.current === requestedSelectionVersion) setLoading(false);
      logger.log('🏢 loadOrganizations: Complete');
    }
  };

  const switchOrganization = async (orgId: string, options: { reload?: boolean } = {}) => {
    logger.log('🔄 switchOrganization: Starting switch to org:', orgId);
    logger.log('🔄 switchOrganization: Current org before switch:', currentOrganization?.id, currentOrganization?.name);

    try {
      setLoading(true);
      setError(null);

      // An accepted invitation may have added a membership since the list loaded.
      let org = organizations.find(o => o.id === orgId);
      if (!org) {
        const updatedOrganizations = await organizationAPI.getOrganizations();
        setOrganizations(updatedOrganizations);
        org = updatedOrganizations.find(o => o.id === orgId);
      }
      if (!org) {
        logger.error('❌ Organization not found in list:', orgId);
        logger.log('Available orgs:', organizations.map(o => ({ id: o.id, name: o.name })));
        throw new Error('Organization not found');
      }

      logger.log('🔄 switchOrganization: Found org to switch to:', org.name);
      logger.log('🔄 switchOrganization: Calling backend /switch endpoint...');

      // Call backend to get new tokens with org context
      // This is REQUIRED - the JWT tokens contain the organization_id claim
      const response = await organizationAPI.switchOrganization(orgId);
      // Ignore organization lists requested before this successful switch.
      selectionVersion.current += 1;

      logger.log('🔄 switchOrganization: Backend response:', response);
      logger.log('🔄 switchOrganization: New access_token received:', !!response.access_token);
      logger.log('🔄 switchOrganization: New refresh_token received:', !!response.refresh_token);

      // IMPORTANT: Store the new tokens (they contain the new org_id in JWT claims)
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);

      // Update localStorage with new org
      localStorage.setItem('current_organization_id', orgId);
      localStorage.setItem('enterprise_org_id', orgId);

      // Store user role for API headers
      const userRole = (org as any).user_role || 'member';
      localStorage.setItem('enterprise_user_role', userRole);

      logger.log('🔄 switchOrganization: LocalStorage updated:');
      logger.log('  - current_organization_id:', localStorage.getItem('current_organization_id'));
      logger.log('  - enterprise_org_id:', localStorage.getItem('enterprise_org_id'));
      logger.log('  - enterprise_user_role:', localStorage.getItem('enterprise_user_role'));

      // Update current organization in state
      setCurrentOrganization(org);

      // Invitation callers navigate to the dashboard before reloading.
      if (options.reload !== false) {
        setTimeout(() => window.location.reload(), 100);
      }
    } catch (err: any) {
      logger.error('❌ Failed to switch organization:', err);
      logger.error('❌ Error details:', err.message, err.status, err.response);
      setError(err.message || 'Failed to switch organization');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const refreshOrganizations = async () => {
    await loadOrganizations();
  };

  return (
    <OrganizationContext.Provider
      value={{
        currentOrganization,
        organizations,
        loading,
        error,
        switchOrganization,
        refreshOrganizations,
        setCurrentOrganization,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
};

